import { Component, inject, OnInit, signal, Signal, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { SavedReport } from '../../core/models/report.model';
import { ReportSessionService } from '../../core/reports/report-session.service';
import { SavedReportService } from '../../core/reports/saved-report.service';
import { AuthService } from '../../core/services/auth.service';
import { LoggerService } from '../../core/services/logger.service';
import { PermissionService } from '../../core/services/permission.service';
import { ToastService } from '../../core/services/toast.service';
import { TranslationService } from '../../core/services/translation.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

/**
 * Reports Home — lists saved reports (own + org-shared) and links to the
 * builder. Run loads a report into the session and opens the results view.
 *
 * Ownership gates management: only a report's creator can edit, delete, or
 * change its sharing. Sharing itself additionally requires `reports.share`.
 */
@Component({
  selector: 'app-reports-home',
  standalone: true,
  imports: [
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    IconComponent,
    ButtonComponent,
    HasPermissionDirective,
    TranslatePipe,
  ],
  templateUrl: './reports-home.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './reports-home.component.css',
})
export class ReportsHomeComponent implements OnInit {
  private router = inject(Router);
  private savedReports = inject(SavedReportService);
  private session = inject(ReportSessionService);
  private auth = inject(AuthService);
  private permissions = inject(PermissionService);
  private toast = inject(ToastService);
  private translate = inject(TranslationService);
  private log = inject(LoggerService).getLogger('reports-home');

  reports!: Signal<SavedReport[]>;
  loading!: Signal<boolean>;

  readonly confirmingDeleteId = signal<string | null>(null);

  ngOnInit(): void {
    const query = this.savedReports.createVisibleQuery();
    this.reports = query.data;
    this.loading = query.loading;
  }

  /** True when the current user owns the report (may edit/delete/share it). */
  canManage(report: SavedReport): boolean {
    const userId = this.auth.getCurrentUserId();
    return !!userId && report.created_by === userId;
  }

  canShare(): boolean {
    return this.permissions.has('reports.share');
  }

  createNew(): void {
    // Start blank — don't inherit any in-progress report from the session.
    this.session.clear();
    this.router.navigate(['/reports/new']);
  }

  run(report: SavedReport): void {
    this.session.set(report.definition);
    this.router.navigate(['/reports', report.id, 'results']);
  }

  edit(report: SavedReport): void {
    this.router.navigate(['/reports', report.id]);
  }

  requestDelete(report: SavedReport): void {
    this.confirmingDeleteId.set(report.id);
  }

  cancelDelete(): void {
    this.confirmingDeleteId.set(null);
  }

  async confirmDelete(report: SavedReport): Promise<void> {
    try {
      await this.savedReports.remove(report.id);
      this.toast.success(this.translate.translate('reports.home.deleted'));
    } catch (error) {
      this.log.error('Failed to delete report', error);
      this.toast.error(this.translate.translate('reports.home.deleteError'));
    } finally {
      this.confirmingDeleteId.set(null);
    }
  }

  async toggleShare(report: SavedReport): Promise<void> {
    if (!this.canShare()) return;
    try {
      if (report.visibility === 'org_shared') {
        await this.savedReports.unshare(report.id);
        this.toast.success(this.translate.translate('reports.home.unshared'));
      } else {
        await this.savedReports.share(report.id);
        this.toast.success(this.translate.translate('reports.home.shared'));
      }
    } catch (error) {
      this.log.error('Failed to change report sharing', error);
      this.toast.error(this.translate.translate('reports.home.shareError'));
    }
  }
}
